import { IController, IFilterService } from 'angular';
import { find, reduce, size } from 'lodash';
import { ImSchool } from 'Src/ng2/shared/services/im-models/im-school';
import { ICreditRequirement } from '../../../../../../ng2/shared/constants/credit-requirements.constant';
import { SubjectAreas } from '../../../../../../ng2/shared/constants/subject-areas.constant';
import { TCreditSubjAreas } from '../../../../../../ng2/shared/typings/interfaces/school.interface';
import { IStudent, ITranscriptCourse } from '../../../../../../ng2/shared/typings/interfaces/student.interface';
import {
  CreditGapCoursePickerModal,
  ITermDetails,
} from '../../../../../shared/modals/credit-gap-course-picker/credit-gap-course-picker-modal.service';

type TCourseHistoryTableCols = 'subject' | 'school' | 'date' | 'courseCode' | 'credit' | 'mark';

export interface ICourseHistoryCourse {
  subject: string;
  subjectArea: TCreditSubjAreas;
  school: string;
  date: string;
  courseCode: string;
  credit: number;
  mark: string;
  cssClass: string;
}

interface ISubjectAreaKeysHash {
  [subjectAreaKey: string]: boolean;
}

export class StudentCreditGapsPanelCourseHistoryCtrl implements IController {
  static $inject: string[] = ['$filter', 'Rollbar', 'ImSchool', 'CreditGapCoursePickerModal'];

  // bindings
  public isRestricted;
  public gapPlans;
  private courseDiffs;
  private school;
  private student: IStudent;
  private sharedState: any;

  // class properties
  courseHistoryForCreditReq;
  humanReadableForSubjectHash;
  activeSort: TCourseHistoryTableCols;
  reverseSort: boolean;
  selectedCreditReqHuman: string;

  // for modals
  public termYearTypes = {
    CURRENT: {
      termType: 'CURRENT',
      value: null,
    } as ITermDetails,
    NEXT: {
      termType: 'NEXT',
      value: null,
    } as ITermDetails,
    FUTURE: {
      termType: 'FUTURE',
      value: null,
    } as ITermDetails,
  };

  constructor(
    private $filter: IFilterService,
    private Rollbar,
    private ImSchool: ImSchool,
    private coursePickerModal: CreditGapCoursePickerModal,
  ) {}

  $onInit() {
    const { selectedCreditReq }: { selectedCreditReq: ICreditRequirement } = this.sharedState;

    this.humanReadableForSubjectHash = {}; // used by getHumanNameForSubject()
    this.activeSort = 'subject';
    this.reverseSort = false;
    this.setValuesForCreditReq(selectedCreditReq);
    this.setTermYearTypes();
  }

  $onChanges(changesObj) {
    const { sharedState } = changesObj;

    if (!sharedState) return;

    const { previousValue, currentValue } = sharedState;
    const sharedStateChangesObjInitialized = !!size(previousValue);

    if (sharedStateChangesObjInitialized) {
      const oldCreditReq: ICreditRequirement = previousValue && previousValue.selectedCreditReq;
      const newCreditReq: ICreditRequirement = currentValue && currentValue.selectedCreditReq;

      if (newCreditReq !== oldCreditReq) {
        this.setValuesForCreditReq(newCreditReq);
      }
    }
  }

  setTermYearTypes() {
    this.termYearTypes.CURRENT.value = this.ImSchool.getCurrentTermYear(this.school);
    this.termYearTypes.NEXT.value = this.ImSchool.getNextTermYear(this.school);
    // Leave termYearTypes.FUTURE as null
  }

  public sortBy(colName: TCourseHistoryTableCols) {
    const newActiveSortExists = this.activeSort !== colName;

    if (newActiveSortExists) {
      this.activeSort = colName;
      this.reverseSort = false;
    } else {
      this.reverseSort = !this.reverseSort;
    }

    this.courseHistoryForCreditReq = this.$filter('orderBy')(this.courseHistoryForCreditReq, colName, this.reverseSort);
  }

  public openMenu = ($mdOpenMenu, ev) => {
    $mdOpenMenu(ev);
  };

  public showFindACourseModal(course: ICourseHistoryCourse, termDetails: ITermDetails) {
    const { courseCode, subjectArea } = course;

    this.coursePickerModal.show(this.courseDiffs, this.gapPlans, termDetails, this.school, this.student, {
      courseCodeWithSection: courseCode,
      subjectArea,
    });
  }

  private setValuesForCreditReq(creditRequirement: ICreditRequirement) {
    this.courseHistoryForCreditReq = this.getCourseHistoryForCreditReq(creditRequirement);
    this.selectedCreditReqHuman = creditRequirement.humanShort.toUpperCase();
  }

  private getCourseHistoryForCreditReq(creditRequirement: ICreditRequirement): ICourseHistoryCourse[] {
    const { transcript } = this.student.creditDetails;
    const subjectAreaKeysHash = this.getSubjectAreaKeysHash(creditRequirement);
    const courses = reduce(
      transcript,
      (acc, course) => {
        const courseSubject = course.subjectArea;

        if (subjectAreaKeysHash[courseSubject]) {
          const courseInfoForCreditReq = this.getCourseInfoForCreditReq(course);

          acc.push(courseInfoForCreditReq);
        }

        return acc;
      },
      [],
    );

    return courses;
  }

  private getCourseInfoForCreditReq(course: ITranscriptCourse): ICourseHistoryCourse {
    const subject = this.getHumanReadableForSubject(course.subjectArea);
    const subjectArea = course.subjectArea;
    const school = course.courseSchoolId;
    const date = course.schoolYear + '/' + course.term;
    const courseCode = course.courseCode;
    const credit = course.creditValue;
    const mark = course.mark.string;
    const cssClass = this.getCourseCssClass(course);

    return { subject, subjectArea, school, date, courseCode, credit, mark, cssClass };
  }

  private getSubjectAreaKeysHash(creditRequirement: ICreditRequirement): ISubjectAreaKeysHash {
    const { subjectAreaKeys } = creditRequirement;
    const subjectAreaKeysHash = reduce(
      subjectAreaKeys,
      (acc: ISubjectAreaKeysHash, subjectAreaKey: string) => {
        acc[subjectAreaKey] = true;
        return acc;
      },
      {},
    );

    return subjectAreaKeysHash;
  }

  private getHumanReadableForSubject(subjectKey: TCreditSubjAreas): string {
    let humanReadable: string = this.humanReadableForSubjectHash[subjectKey];

    if (!humanReadable) {
      const subjectArea = find(SubjectAreas, { camelCase: subjectKey });
      humanReadable = this.humanReadableForSubjectHash[subjectKey] = subjectArea.human;
    }

    return humanReadable;
  }

  private getCourseCssClass(course: ITranscriptCourse) {
    switch (course.passFailEquivalent) {
      case 'P':
        return 'pass';
      case 'F':
        return 'fail';
      default:
        return '';
    }
  }
}
